import React, {
  Fragment, memo, useMemo,
} from 'react';
import {
  Configuration, Section, SectionTypes, WebsiteEvent,
} from '../../resources/modelTypes';
import { SelectedCollection, SelectedRecord } from '../../resources/selectors';
import { sortRecordsByCreatedAtAndPosition } from '../../website/utils';
import { DisplaySectionWebsite } from './DisplaySection';
import { DisplayEvent } from './DisplayEvent';
import { DisplayWrapper } from './DisplayWrapper';
import { PageSlugs } from '../types';
import { DisplayRSVP } from './DisplayRSVP';
import { AttendantSection, DisplayAttendantSection } from './section-types/DisplayAttendantSection';
import { DisplayDividerSection } from './section-types/DisplayDividerSection';
import { selectors } from '../cypressSelectors';
import { DisplayMap } from './section-types/DisplayMap';

export type DisplayWebsiteContentProps = {
  configuration: Configuration
  eventCollection: SelectedCollection<WebsiteEvent> | null,
  sectionCollection: SelectedCollection<Section>,
  slug: string,
}

const DisplayWebsiteContent = ({
  configuration,
  eventCollection,
  sectionCollection,
  slug,
}: DisplayWebsiteContentProps) => {
  let allItems: (SelectedRecord<WebsiteEvent> | SelectedRecord<Section>)[];
  const stagedSection = sectionCollection.resource.staged?.create?.[0];
  const stagedPageId = stagedSection?.data?.page;
  const sectionCollectionPageId = sectionCollection.params.page;
  const stagedEvent = eventCollection?.resource.staged?.create[0];
  const eventPageName = eventCollection?.resource?.name;

  if (eventCollection?.items) {
    allItems = [
      ...eventCollection.items,
      ...sectionCollection.items,
    ];

    if (stagedEvent && eventPageName === PageSlugs.Events) {
      allItems.push(stagedEvent);
    }
  } else {
    allItems = sectionCollection.items;
  }

  if (stagedSection && stagedPageId === sectionCollectionPageId) {
    allItems.push(stagedSection);
  }

  sortRecordsByCreatedAtAndPosition(allItems);

  const extraComponentsForSlug = useMemo(() => {
    if (slug === PageSlugs.RSVP) {
      return (
        <DisplayRSVP
          configuration={configuration}
          isPreview={false}
        />
      );
    }

    return null;
  }, [
    configuration,
    slug,
  ]);

  const sectionElements: React.ReactNode[] = [];

  for (let index = 0; index < allItems.length; index++) {
    const current = allItems[index] as SelectedRecord<WebsiteEvent> | SelectedRecord<Section>;
    const isEvent = !('sectionType' in current.data);

    if (isEvent) {
      const event = current.data as WebsiteEvent;

      if (event.private) {
        continue;
      }

      sectionElements.push(
        <div
          data-cy={`${selectors.website_displaySection}_event`}
          id={`section-${event.id || 'new'}`}
          key={`collapsed${event.id}`}
        >
          <DisplayEvent
            configuration={configuration}
            event={event}
          />
          {configuration.showPageDividers && <DisplayDividerSection hideLast={index === allItems.length - 1} />}
        </div>

      );
      continue;
    }

    const section = current.data as Section;

    if (section.sectionType === SectionTypes.Attendant) {
      const attendantGroup = [
        section,
      ] as AttendantSection[];

      const nextSection = allItems[index + 1]?.data as AttendantSection;

      // If next section is also an Attendant, group them for potential grid display
      if (nextSection && nextSection.sectionType === SectionTypes.Attendant) {
        attendantGroup.push(nextSection);
        index++;
      }

      sectionElements.push(
        <div
          data-cy={`${selectors.website_displaySection}_attendantGroup`}
          id={`section-${section.id || 'new'}`}
          key={`attendantGroup${section.id}`}
        >
          <DisplayAttendantSection
            configuration={configuration}
            sections={attendantGroup}
          />
        </div>
      );
      continue;
    }

    const shouldDisplayDivider = configuration.showPageDividers && [
      SectionTypes.Accommodation,
      SectionTypes.Registry,
      SectionTypes.CashRegistry,
    ].includes(section.sectionType);

    // All other Section types displayed here
    sectionElements.push(
      <Fragment
        key={`collapsed${section.id}`}
      >
        <div
          data-cy={`${selectors.website_displaySection}_${section.sectionType}`}
          id={`section-${section.id || 'new'}`}
        >
          <DisplaySectionWebsite
            configuration={configuration}
            section={section}
          />
        </div>
        {shouldDisplayDivider && <DisplayDividerSection hideLast />}
      </Fragment>
    );
  }

  return (
    <DisplayWrapper
      textAlignment={configuration.textAlignment}
    >
      {extraComponentsForSlug}
      {sectionElements}
      {
        slug === PageSlugs.Events && configuration.showMap && eventCollection && (
          <DisplayMap
            events={eventCollection.items}
          />
        )
      }
    </DisplayWrapper>
  );
};

const MemoizedDisplayWebsiteContent = memo(DisplayWebsiteContent);

export { MemoizedDisplayWebsiteContent as DisplayWebsiteContent };
